import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  closeChatWindow,
  openChatWindow,
  selectFrontChatWindowOpen,
  setUnreadCount,
} from '../store';
import type { FrontChat } from '../types';

/**
 * Hook to handle Front Chat event listeners and sync with Redux
 */
export const useFrontChatEvents = (frontChat: FrontChat | undefined, isInitialized: boolean) => {
  const dispatch = useDispatch();
  const windowOpen = useSelector(selectFrontChatWindowOpen);

  // Sync Redux windowOpen state with FrontChat visibility
  useEffect(() => {
    if (!frontChat || !isInitialized) {
      return;
    }

    if (windowOpen) {
      frontChat('show');
    } else {
      frontChat('hide');
    }
  }, [frontChat, isInitialized, windowOpen]);

  // Listen for user-driven window visibility changes
  useEffect(() => {
    if (!frontChat || !isInitialized) {
      return;
    }

    const handler = ({ is_window_visible }: { is_window_visible: boolean }) => {
      if (is_window_visible) {
        dispatch(openChatWindow());
      } else {
        dispatch(closeChatWindow());
      }
    };

    frontChat('onWindowVisibilityChanged', handler);

    // Note: FrontChat SDK doesn't provide an unsubscribe method
  }, [frontChat, isInitialized, dispatch]);

  // Listen for unread count changes
  useEffect(() => {
    if (!frontChat || !isInitialized) {
      return;
    }

    const unreadHandler = ({ unread_count }: { unread_count: number }) => {
      dispatch(setUnreadCount(unread_count));
    };

    frontChat('onUnreadChange', unreadHandler);

    // Note: FrontChat SDK doesn't provide an unsubscribe method
  }, [frontChat, isInitialized, dispatch]);
};
